import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { FileText, XCircle, CreditCard, Phone } from "lucide-react";
import {
  useDeletePaymentAgreement,
  usePaymentAgreementDetail,
} from "@features/cx/outstanding/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useNavigate } from "react-router-dom";

interface InstallmentSchedule {
  id: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  status: "Pending" | "Paid" | "Overdue";
  paidDate?: string;
  paidAmount?: number;
}

interface AgreementDetails {
  id: string;
  createdDate: string;
  totalAmount: number;
  downPayment: number;
  monthlyPayment: number;
  installments: number;
  startDate: string;
  endDate: string;
  status: "Active" | "Completed" | "Cancelled";
  createdBy: string;
  notes?: string;
}

interface InstallmentsTabProps {
  account: any;
  agreementDetails: AgreementDetails;
  installmentSchedule: InstallmentSchedule[];
  onViewAgreementDocument: () => void;
  onCancelAgreement: () => void;
  formatCurrency: (amount: number) => string;
  formatDate: (dateString: string) => string;
}

export const InstallmentsTab = ({
  account,
  agreementDetails,
  onViewAgreementDocument,
  onCancelAgreement,
  formatCurrency,
  formatDate,
}: InstallmentsTabProps) => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: agreementDetail } = usePaymentAgreementDetail({
    remoteUtilityId: Number(remoteUtilityId),
    remoteBillId: Number(account?.billData?.id),
  });
  
  const getInstallmentStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "Overdue":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      case "Pending":
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
      default:
        return "";
    }
  };

  const getAgreementStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "Completed":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      case "Cancelled":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      default:
        return "";
    }
  };

  // Check if installments exist
  const hasInstallments = agreementDetail?.result?.installments && 
    agreementDetail.result.installments.length > 0;

  return (
    <div className="space-y-4">
      {/* Agreement Information Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-base">
                Payment Agreement Information
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Agreement ID: {agreementDetail?.result?.agreementNo || "N/A"}
              </p>
            </div>
            <Badge
              variant="outline"
              className={getAgreementStatusBadgeColor(
                agreementDetail?.result?.statusDisplay
              )}
            >
              {agreementDetail?.result?.statusDisplay}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <span className="text-muted-foreground">Created Date:</span>
              <p className="font-medium">
                {agreementDetail?.result?.createdDate || "N/A"}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Created By:</span>
              <p className="font-medium">
                {agreementDetail?.result?.createdUserRemoteName || "N/A"}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Total Amount:</span>
              <p className="font-medium">
                {formatCurrency(agreementDetail?.result?.totalAgreementAmount || 0)}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Monthly Payment:</span>
              <p className="font-medium">
                {formatCurrency(agreementDetail?.result?.monthlyPayment || 0)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onViewAgreementDocument}
              className="flex-1"
            >
              <FileText className="mr-2 h-4 w-4" />
              View Agreement Document
            </Button>
            {agreementDetails.status === "Active" && (
              <Button
                variant="destructive"
                onClick={onCancelAgreement}
                className="flex-1"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Cancel Agreement
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Installment Schedule */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Payment Agreement Installments</h3>
        {hasInstallments && (
          <Badge variant="outline" className="text-xs">
            {
              agreementDetail?.result?.installments?.filter(
                (i) => i.statusDisplay === "Paid"
              ).length
            }{" "}
            of {agreementDetail?.result?.installments?.length} paid
          </Badge>
        )}
      </div>

      {hasInstallments ? (
        <div className="space-y-3">
          {agreementDetail?.result?.installments?.map((installment) => (
            <div key={installment.id} className="bg-muted/40 p-3 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">
                    Installment #{installment.installmentNo}
                  </span>
                  <Badge
                    variant="outline"
                    className={getInstallmentStatusBadgeColor(
                      installment.statusDisplay
                    )}
                  >
                    {installment.statusDisplay}
                  </Badge>
                </div>
                <span className="font-medium">
                  {formatCurrency(installment.installmentAmount)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                {installment.statusDisplay === "Paid" && installment.paidDate && (
                  <div>
                    <span className="text-muted-foreground">Paid Date:</span>
                    <p className="font-medium text-green-600">
                      {formatDate(installment.paidDate)}
                    </p>
                  </div>
                )}

                {installment.statusDisplay === "Paid" &&
                  installment.paidAmount && (
                    <div>
                      <span className="text-muted-foreground">Paid Amount:</span>
                      <p className="font-medium text-green-600">
                        {formatCurrency(installment.paidAmount)}
                      </p>
                    </div>
                  )}

                {installment.statusDisplay === "Overdue" && (
                  <div>
                    <span className="text-muted-foreground">Days Overdue:</span>
                    <p className="font-medium text-red-600">
                      {Math.floor(
                        (new Date().getTime() -
                          new Date(installment.dueDate).getTime()) /
                          (1000 * 60 * 60 * 24)
                      )}{" "}
                      days
                    </p>
                  </div>
                )}
              </div>

              {installment.statusDisplay === "Overdue" && (
                <div className="mt-2 pt-2 border-t">
                  <Button size="sm" className="mr-2" onClick={() => navigate(`/cx/payments/create`)}>
                    <CreditCard className="mr-2 h-3 w-3" />
                    Record Payment
                  </Button>
                  <Button size="sm" variant="outline">
                    <Phone className="mr-2 h-3 w-3" />
                    Contact Customer
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>No installments found</p>
        </div>
      )}
    </div>
  );
};